// src/pages/Press.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { 
  press,
  getFeaturedPress,
  pressStats,
  mediaPartners,
  type PressItem
} from '@/data/mock/press';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  ExternalLink,
  Newspaper,
  Video,
  Mic,
  FileText,
  Star,
  Eye,
  TrendingUp,
  Users,
  Award,
  BarChart3,
  Filter,
  X,
  Play,
  Download,
  Mail,
  Phone,
  MessageCircle,
  Globe,
  Image as ImageIcon,
  Sparkles,
  Building2,
  Search
} from 'lucide-react';

const Press = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [selectedType, setSelectedType] = useState<'all' | PressItem['type']>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Type options
  const types = [
    { value: 'all' as const, label: { en: 'All', ar: 'الكل' }, icon: Newspaper },
    { value: 'article' as const, label: { en: 'Articles', ar: 'مقالات' }, icon: FileText },
    { value: 'video' as const, label: { en: 'Videos', ar: 'فيديوهات' }, icon: Video },
    { value: 'interview' as const, label: { en: 'Interviews', ar: 'مقابلات' }, icon: Mic },
    { value: 'documentary' as const, label: { en: 'Documentaries', ar: 'وثائقي' }, icon: Play },
    { value: 'press-release' as const, label: { en: 'Press Releases', ar: 'بيانات صحفية' }, icon: FileText },
  ];

  const categories = [
    { value: 'all', label: { en: 'All Categories', ar: 'جميع الفئات' } },
    { value: 'achievement', label: { en: 'Achievements', ar: 'إنجازات' } },
    { value: 'partnership', label: { en: 'Partnerships', ar: 'شراكات' } },
    { value: 'event', label: { en: 'Events', ar: 'فعاليات' } },
    { value: 'profile', label: { en: 'Profiles', ar: 'ملفات' } },
    { value: 'news', label: { en: 'News', ar: 'أخبار' } },
  ];

  // Filter press items
  const filteredPress = useMemo(() => {
    return press.filter(item => {
      const matchesType = selectedType === 'all' || item.type === selectedType;
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesSearch = searchQuery === '' ||
        item.title.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.title.ar.includes(searchQuery) ||
        item.publication.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesType && matchesCategory && matchesSearch;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [selectedType, selectedCategory, searchQuery]);

  // Featured press
  const featuredPress = getFeaturedPress();

  // Statistics
  const stats = [
    {
      icon: Newspaper,
      value: pressStats.total.toString(),
      label: { en: 'Press Coverage', ar: 'تغطيات صحفية' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Eye,
      value: `${Math.round(pressStats.totalViews / 1000)}K+`,
      label: { en: 'Total Views', ar: 'إجمالي المشاهدات' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Building2,
      value: pressStats.mediaPartners.toString(),
      label: { en: 'Media Partners', ar: 'شركاء إعلاميون' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Star,
      value: pressStats.featured.toString(),
      label: { en: 'Featured Stories', ar: 'قصص مميزة' },
      color: 'from-yellow-500 to-orange-500'
    },
  ];

  // Get type icon
  const getTypeIcon = (type: PressItem['type']) => {
    const icons = {
      'article': FileText,
      'video': Video,
      'interview': Mic,
      'press-release': FileText,
      'feature': Star,
      'documentary': Play
    };
    return icons[type] || FileText;
  };

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('press.title')}
        subtitle={isRTL
          ? 'تغطياتنا الصحفية والإعلامية - قصصنا في الأخبار المحلية والعالمية'
          : 'Our press coverage and media mentions - our stories in local and international news'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.press') }
        ]}
      />

      {/* Press Banner */}
      <section className="py-6 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 border-y border-blue-500/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-3 text-center flex-wrap"
          >
            <Sparkles className="h-6 w-6 text-blue-500" />
            <p className="text-sm font-semibold text-foreground">
              {isRTL
                ? 'تابعنا في وسائل الإعلام المحلية والعالمية - قصص نجاحنا تُلهم الملايين'
                : 'Follow us in local and international media - our success stories inspire millions'
              }
            </p>
            <Sparkles className="h-6 w-6 text-blue-500" />
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <BarChart3 className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'إحصائيات' : 'Statistics'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'وجودنا الإعلامي' : 'Our Media Presence'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-4`}>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Press */}
      {featuredPress.length > 0 && (
        <section className="py-20 bg-surface-1">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-semibold text-yellow-500">
                  {isRTL ? 'تغطيات مميزة' : 'Featured Coverage'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'أبرز التغطيات الإعلامية' : 'Top Media Coverage'}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredPress.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <PressCard item={item} isRTL={isRTL} featured getTypeIcon={getTypeIcon} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Media Partners */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {isRTL ? 'شركاؤنا الإعلاميون' : 'Our Media Partners'}
            </h2>
            <p className="text-muted-foreground">
              {isRTL 
                ? 'موثوق به من قبل وسائل الإعلام الرائدة في جميع أنحاء العالم'
                : 'Trusted by leading media outlets around the world'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-8 max-w-5xl mx-auto">
            {mediaPartners.map((partner, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.1 }}
                className="flex items-center justify-center p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-all"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-h-12 w-auto object-contain filter grayscale hover:grayscale-0 transition-all"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-12 bg-gradient-to-b from-surface-1 to-background top-20 z-40 border-b border-border shadow-lg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-2xl mx-auto">
                <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
                <input
                  type="text"
                  placeholder={isRTL ? 'ابحث في التغطيات الصحفية...' : 'Search press coverage...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full ${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none transition-all`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-all`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Type Filters */}
            <div className="flex justify-center gap-2 mb-6 flex-wrap">
              {types.map((type) => {
                const Icon = type.icon;
                return (
                  <motion.button
                    key={type.value}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedType(type.value)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all font-medium ${
                      selectedType === type.value
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'bg-card border-2 border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {type.label[isRTL ? 'ar' : 'en']}
                  </motion.button>
                );
              })}
            </div>

            {/* Category Filter */}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 rounded-full bg-card border border-border focus:border-primary/50 outline-none cursor-pointer text-sm font-medium"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label[isRTL ? 'ar' : 'en']}
                  </option>
                ))}
              </select>

              {/* Clear Filters */}
              {(selectedType !== 'all' || selectedCategory !== 'all' || searchQuery) && (
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedType('all');
                    setSelectedCategory('all');
                    setSearchQuery('');
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20 transition-all text-sm"
                >
                  <X className="h-4 w-4" />
                  {isRTL ? 'مسح' : 'Clear'}
                </motion.button>
              )}
            </div>

            {/* Results Count */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center"
            >
              <span className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredPress.length} من ${press.length}`
                  : `Showing ${filteredPress.length} of ${press.length}`
                }
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Press Grid */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            {filteredPress.length > 0 ? (
              <motion.div
                key={`${selectedType}-${selectedCategory}-${searchQuery}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredPress.map((item, idx) => (
                  <PressCard
                    key={item.id}
                    item={item}
                    isRTL={isRTL}
                    delay={idx * 0.05}
                    getTypeIcon={getTypeIcon}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-20"
              >
                <Newspaper className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-lg text-muted-foreground">
                  {isRTL ? 'لم يتم العثور على تغطيات' : 'No coverage found'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Press Kit & Contact */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/30 via-accent/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Press Kit */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-3xl bg-card border-2 border-border shadow-xl"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
                <Download className="h-8 w-8 text-primary" />
              </div>

              <h3 className="text-2xl font-bold mb-4">
                {isRTL ? 'مجموعة الصحافة' : 'Press Kit'}
              </h3>

              <p className="text-muted-foreground mb-6">
                {isRTL
                  ? 'احصل على شعاراتنا، صورنا، ومعلومات الأكاديمية لاستخدامها الإعلامي'
                  : 'Get our logos, photos, and academy information for media use'
                }
              </p>

              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  <span>{isRTL ? 'صور عالية الدقة' : 'High-resolution images'}</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>{isRTL ? 'حقائق ومعلومات' : 'Fact sheets'}</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Award className="h-4 w-4 text-primary" />
                  <span>{isRTL ? 'شعارات وأيقونات' : 'Logos and icons'}</span>
                </li>
              </ul>

              <Button className="w-full btn-glow">
                <Download className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'تحميل مجموعة الصحافة' : 'Download Press Kit'}
              </Button>
            </motion.div>

            {/* Press Contact */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-3xl bg-card border-2 border-border shadow-xl"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
                <MessageCircle className="h-8 w-8 text-primary" />
              </div>

              <h3 className="text-2xl font-bold mb-4">
                {isRTL ? 'اتصل بقسم الإعلام' : 'Press Contact'}
              </h3>

              <p className="text-muted-foreground mb-6">
                {isRTL
                  ? 'للاستفسارات الصحفية والإعلامية، تواصل مع فريقنا'
                  : 'For press inquiries and media requests, contact our team'
                }
              </p>

              <div className="space-y-4">
                <a href="mailto:press@championsacademy.ps" className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-all">
                  <Mail className="h-5 w-5 text-primary" />
                  <span className="text-sm">press@championsacademy.ps</span>
                </a>

                <a href="tel:+970123456789" className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-all">
                  <Phone className="h-5 w-5 text-primary" />
                  <span className="text-sm">+970 123 456 789</span>
                </a>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5">
                  <Globe className="h-5 w-5 text-primary" />
                  <span className="text-sm">{isRTL ? 'متاح 24/7' : 'Available 24/7'}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Press Card Component
const PressCard = ({ 
  item, 
  isRTL, 
  featured = false,
  delay = 0,
  getTypeIcon
}: { 
  item: PressItem; 
  isRTL: boolean;
  featured?: boolean;
  delay?: number;
  getTypeIcon: (type: PressItem['type']) => any;
}) => {
  const TypeIcon = getTypeIcon(item.type);

  return (
    <motion.a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -8 }}
      className={`block bg-card border-2 ${featured ? 'border-yellow-500/50' : 'border-border'} rounded-2xl overflow-hidden hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 group`}
    >
      {/* Image */}
      <div className="aspect-video bg-muted relative overflow-hidden">
        <img
          src={item.image}
          alt={item.title[isRTL ? 'ar' : 'en']}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
        />
        
        {/* Type Badge */}
        <div className="absolute top-4 left-4">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/90 backdrop-blur-sm border border-border">
            <TypeIcon className="h-3 w-3 text-primary" />
            <span className="text-xs font-semibold capitalize">{item.type}</span>
          </div>
        </div>

        {/* Featured Badge */}
        {featured && (
          <div className="absolute top-4 right-4">
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-yellow-500/90 backdrop-blur-sm">
              <Star className="h-3 w-3 text-white fill-white" />
              <span className="text-xs font-bold text-white">FEATURED</span>
            </div>
          </div>
        )}

        {/* External Link */}
        <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center border border-border opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink className="h-4 w-4" />
        </div>

        {/* Video Play Button */}
        {(item.type === 'video' || item.type === 'documentary') && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
            <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
              <Play className="h-8 w-8 text-primary" />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Meta */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{new Date(item.date).toLocaleDateString(isRTL ? 'ar' : 'en')}</span>
          </div>
          {item.views && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Eye className="h-3 w-3" />
              <span>{Math.round(item.views / 1000)}K</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {item.title[isRTL ? 'ar' : 'en']}
        </h3>

        {/* Publication */}
        <p className="text-sm text-primary font-semibold mb-3">{item.publication}</p>

        {/* Excerpt */}
        <p className="text-muted-foreground text-sm line-clamp-3 leading-relaxed">
          {item.excerpt[isRTL ? 'ar' : 'en']}
        </p>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
            {item.tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.a>
  );
};

export default Press;
